import { memo, useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Banner } from '../../components/Banner/Banner'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import MultiSelect from '../../components/MultiSelect'
import Select from '../../components/Select'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'
import { useAppointmentNoteFormRxFieldsEnabled } from '../../hooks/useAppointmentNoteFormRxFieldsEnabled'
import {
  OTHER_SPECIALIST_OPTION,
  SPECIALIST_SELECT_OPTIONS,
  SpecialistSelectValue,
  deriveReferToSpecialistValue,
  hydrateReferToSpecialistSelection,
} from '../../utils/refer-to-specialist.utils'
import './styles.scss'

const OptionsIds = {
  LABS_ORDERED: 'labsOrdered',
  IMAGING_ORDERED: 'imagingOrdered',
  NO_LABS_IMAGING_ORDERED: 'noLabsImagingOrdered',
  REFER_TO_SPECIALIST: 'referToSpecialist',
  RX_INTEREST: 'rxInterest',
} as const

type OptionId = (typeof OptionsIds)[keyof typeof OptionsIds]

export enum NoFollowUpReasonEnum {
  PATIENT_IS_CANCELLING = 'Patient is cancelling',
  PATIENT_IS_PREGNANT = 'Patient is pregnant',
  PATIENT_IS_BECOMING_NUTRITION_ONLY = 'Patient is becoming Nutrition only',
  SEEKING_EXTERNAL_CARE = 'Seeking external care',
  APPT_ALREADY_SCHEDULED = 'Appt already scheduled',
  OTHER = 'Other',
}

export enum RxInterestCategoryEnum {
  GLP = 'glp',
  DERMATOLOGY = 'dermatology',
  HRT = 'hrt',
  HAIR_LOSS = 'hair_loss',
  SEXUAL_HEALTH = 'sexual_health',
}

type RxInterestMedicationOption = {
  name: string
  category: RxInterestCategoryEnum
}

/** Provider picks by name; category is derived for CIO / suppress logic. */
export const RX_INTEREST_MEDICATIONS: RxInterestMedicationOption[] = [
  { name: 'Semaglutide', category: RxInterestCategoryEnum.GLP },
  { name: 'Tirzepatide', category: RxInterestCategoryEnum.GLP },
  { name: 'Tretinoin', category: RxInterestCategoryEnum.DERMATOLOGY },
  { name: 'Even Tone Corrector', category: RxInterestCategoryEnum.DERMATOLOGY },
  { name: 'Estradiol', category: RxInterestCategoryEnum.HRT },
  { name: 'Hair Growth Solution', category: RxInterestCategoryEnum.HAIR_LOSS },
  {
    name: 'Topical Arousal Cream',
    category: RxInterestCategoryEnum.SEXUAL_HEALTH,
  },
]

export function getRxInterestCategories(
  medicationNames: string[]
): RxInterestCategoryEnum[] {
  const categories = RX_INTEREST_MEDICATIONS.filter((med) =>
    medicationNames.includes(med.name)
  ).map((med) => med.category)

  return Array.from(new Set(categories))
}

const Orders: React.FC = () => {
  const showRxInterestFields = useAppointmentNoteFormRxFieldsEnabled()
  const {
    updateTestsResponse,
    updateNoReasonFollowUp,
    selectedAppointment,
    noFollowUpReason,
    showNoFollowUp,
    noFollowUpOtherReason,
    updateShowFollowUp,
    nextScreen,
    updateNoFollowUpOtherReason,
    labsResponse,
    imagingResponse,
    referToSpecialist,
    rxInterest,
    rxInterestMedications: savedRxInterestMedications,
    isEditing,
  } = usePostAppointmentContext()
  const [options, setOptions] = useState<Record<OptionId, boolean>>({
    [OptionsIds.LABS_ORDERED]: labsResponse ?? false,
    [OptionsIds.IMAGING_ORDERED]: imagingResponse ?? false,
    [OptionsIds.NO_LABS_IMAGING_ORDERED]: false,
    [OptionsIds.REFER_TO_SPECIALIST]: !!referToSpecialist,
    [OptionsIds.RX_INTEREST]: rxInterest ?? false,
  })

  // An edit cannot express free text as an op, so it must not carry Other in
  // the selection either: a chip with no option renders nothing and cannot be
  // removed, and the Next guard then waits on a box that is not on screen.
  const [selectedSpecialists, setSelectedSpecialists] = useState<
    SpecialistSelectValue[]
  >(() => {
    const hydrated =
      hydrateReferToSpecialistSelection(referToSpecialist).selectedSpecialists
    return isEditing
      ? hydrated.filter((value) => value !== OTHER_SPECIALIST_OPTION)
      : hydrated
  })
  const [otherSpecialistText, setOtherSpecialistText] = useState(
    () =>
      hydrateReferToSpecialistSelection(referToSpecialist).otherSpecialistText
  )

  const [rxInterestMedications, setRxInterestMedications] = useState<string[]>(
    savedRxInterestMedications ?? []
  )

  const clearSpecialistSelection = () => {
    setSelectedSpecialists([])
    setOtherSpecialistText('')
  }

  const handleCheckboxChange = (id: keyof typeof options) => {
    /*
      if No labs/imaging ordered is checked, then we has to uncheck labs and imaging
      if labs or imaging is checked, then we has to uncheck no labs/imaging ordered
    */
    setOptions((prev) => {
      const updated = { ...prev }

      switch (id) {
        case OptionsIds.LABS_ORDERED:
        case OptionsIds.IMAGING_ORDERED:
          updated[id] = !prev[id]

          if (
            updated[OptionsIds.LABS_ORDERED] ||
            updated[OptionsIds.IMAGING_ORDERED]
          ) {
            updated[OptionsIds.NO_LABS_IMAGING_ORDERED] = false
          }

          return updated

        case OptionsIds.NO_LABS_IMAGING_ORDERED:
          updated[OptionsIds.NO_LABS_IMAGING_ORDERED] =
            !prev[OptionsIds.NO_LABS_IMAGING_ORDERED]

          if (updated[OptionsIds.NO_LABS_IMAGING_ORDERED]) {
            updated[OptionsIds.LABS_ORDERED] = false
            updated[OptionsIds.IMAGING_ORDERED] = false
          }

          return updated

        case OptionsIds.REFER_TO_SPECIALIST:
          updated[id] = !prev[id]

          if (prev[id]) {
            clearSpecialistSelection()
          }

          return updated

        case OptionsIds.RX_INTEREST:
          updated[id] = !prev[id]

          if (prev[id]) {
            setRxInterestMedications([])
          }

          return updated

        default:
          return prev
      }
    })
  }

  const handleSpecialistChange = (nextValues: string[]) => {
    const next = nextValues as SpecialistSelectValue[]
    setSelectedSpecialists(next)

    if (!next.includes(OTHER_SPECIALIST_OPTION)) {
      setOtherSpecialistText('')
    }
  }

  const handleConfirm = useCallback(
    () => nextScreen(undefined, { noFollowUp: showNoFollowUp }),
    [noFollowUpReason]
  )

  const handleNext = useCallback(() => {
    const referToSpecialistValue = deriveReferToSpecialistValue({
      checked: options[OptionsIds.REFER_TO_SPECIALIST],
      selectedSpecialists,
      otherSpecialistText,
    })

    /*
      When the flag is off the Rx controls are never rendered, so this state still
      holds whatever was hydrated from the note. Passing it through preserves an
      existing draft's Rx data instead of clearing it.
    */
    const rxInterestSelected = options[OptionsIds.RX_INTEREST]

    updateTestsResponse(
      options[OptionsIds.LABS_ORDERED],
      options[OptionsIds.IMAGING_ORDERED],
      referToSpecialistValue,
      rxInterestSelected,
      rxInterestSelected ? rxInterestMedications : []
    )
  }, [
    options,
    selectedSpecialists,
    otherSpecialistText,
    rxInterestMedications,
    selectedAppointment,
    updateTestsResponse,
  ])

  // The wizard's hydration title-cases free text into one joined string, so a
  // diff against it emits removals the backend cannot match. An edit is offered
  // only the fixed list, and any free text the note already carries is left
  // untouched rather than silently rewritten.
  const specialistOptions = useMemo(
    () =>
      isEditing
        ? SPECIALIST_SELECT_OPTIONS.filter(
            (option) => option.value !== OTHER_SPECIALIST_OPTION
          )
        : SPECIALIST_SELECT_OPTIONS,
    [isEditing]
  )

  const isNextDisabled = useMemo(() => {
    const referToSpecialistChecked = options.referToSpecialist
    const referToSpecialistOk =
      !referToSpecialistChecked ||
      (selectedSpecialists.length > 0 &&
        (!selectedSpecialists.includes(OTHER_SPECIALIST_OPTION) ||
          otherSpecialistText.trim() !== ''))

    // An edit may legitimately change nothing but the note text, so the
    // sign-time "record at least one order" rule does not apply to it.
    if (isEditing) {
      return !referToSpecialistOk
    }

    const atLeastOneOptionSelected = Object.entries(options).some(
      ([key, value]) => {
        if (key === OptionsIds.RX_INTEREST && !showRxInterestFields) {
          return false
        }

        return value
      }
    )

    const rxInterestOk =
      !showRxInterestFields ||
      !options[OptionsIds.RX_INTEREST] ||
      (options[OptionsIds.RX_INTEREST] && rxInterestMedications.length > 0)

    return !atLeastOneOptionSelected || !referToSpecialistOk || !rxInterestOk
  }, [
    options,
    selectedSpecialists,
    otherSpecialistText,
    rxInterestMedications,
    showRxInterestFields,
    isEditing,
  ])

  return (
    <div className="orders d-flex justify-content-center align-items-center align-self-center flex-column mt-3">
      {!showNoFollowUp && (
        <div className="d-flex flex-column gap-4 align-items-center">
          <p className="description m-0">
            Did you order or recommend any of the following for the patient?
          </p>
          <div className="d-flex flex-column gap-3 align-items-center">
            <Form className="d-flex flex-column gap-1">
              <Form.Check
                type="checkbox"
                label="Labs Ordered"
                id={OptionsIds.LABS_ORDERED}
                checked={options[OptionsIds.LABS_ORDERED]}
                onChange={() => handleCheckboxChange(OptionsIds.LABS_ORDERED)}
              />
              <Form.Check
                type="checkbox"
                label="Imaging Ordered"
                id={OptionsIds.IMAGING_ORDERED}
                checked={options[OptionsIds.IMAGING_ORDERED]}
                onChange={() =>
                  handleCheckboxChange(OptionsIds.IMAGING_ORDERED)
                }
              />
              {showRxInterestFields && (
                <>
                  <Form.Check
                    type="checkbox"
                    label="Interested in Allara Rx"
                    id={OptionsIds.RX_INTEREST}
                    checked={options[OptionsIds.RX_INTEREST]}
                    onChange={() =>
                      handleCheckboxChange(OptionsIds.RX_INTEREST)
                    }
                  />
                  {options[OptionsIds.RX_INTEREST] && (
                    <MultiSelect
                      className="rx-interest-medications"
                      value={rxInterestMedications}
                      onChange={setRxInterestMedications}
                      placeholder="Select medications"
                      aria-label="Allara Rx medications of interest"
                      options={RX_INTEREST_MEDICATIONS.map((med) => ({
                        value: med.name,
                        label: med.name,
                      }))}
                    />
                  )}
                </>
              )}
              <Form.Check
                type="checkbox"
                checked={options[OptionsIds.REFER_TO_SPECIALIST]}
                onChange={() =>
                  handleCheckboxChange(OptionsIds.REFER_TO_SPECIALIST)
                }
                label="Refer to Specialist"
                id={OptionsIds.REFER_TO_SPECIALIST}
              />
              {options[OptionsIds.REFER_TO_SPECIALIST] && (
                <>
                  <MultiSelect
                    className="refer-to-specialist"
                    value={selectedSpecialists}
                    onChange={handleSpecialistChange}
                    placeholder="Select specialist"
                    aria-label="Refer to specialist"
                    options={specialistOptions}
                  />
                  {!isEditing &&
                    selectedSpecialists.includes(OTHER_SPECIALIST_OPTION) && (
                      <Form.Control
                        type="text"
                        placeholder="Specialist type"
                        id="referToSpecialistOther"
                        value={otherSpecialistText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setOtherSpecialistText(e.target.value)
                        }
                      />
                    )}
                  {isEditing && otherSpecialistText.trim() !== '' && (
                    <p className="description mb-0">
                      {`Also referred to ${otherSpecialistText}. This screen cannot change a free-text referral.`}
                    </p>
                  )}
                </>
              )}

              <Form.Check
                type="checkbox"
                label="No labs/imaging ordered"
                id={OptionsIds.NO_LABS_IMAGING_ORDERED}
                checked={options[OptionsIds.NO_LABS_IMAGING_ORDERED]}
                onChange={() =>
                  handleCheckboxChange(OptionsIds.NO_LABS_IMAGING_ORDERED)
                }
              />
            </Form>
          </div>
          <div className="d-flex gap-3">
            {/* updateShowFollowUp clears labs, imaging, referrals and rx. That
                is right when signing and destructive in an edit, so the
                withdrawal lives on the follow-up step instead. */}
            {!isEditing && (
              <SecondaryButton
                onClick={updateShowFollowUp}
                theme="dark"
                className="px-4 py-2"
              >
                No follow-up
              </SecondaryButton>
            )}
            <PrimaryButton
              onClick={handleNext}
              theme="dark"
              disabled={isNextDisabled}
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      )}

      {showNoFollowUp && (
        <div className="d-flex flex-column gap-4 align-items-center">
          <p className="m-0">
            <strong>
              Are you sure you don&apos;t want to provide a follow up plan for
              this patient?
            </strong>
          </p>
          <Banner
            limitWidth={true}
            variant="info"
            message="Note: If there is no follow-up plan, the patient won’t see anything on their dashboard and won’t be prompted to book with you again. This should only be done if the patient is not continuing with Allara or isn’t a fit for our care."
          />

          <div className="d-flex flex-column gap-3">
            <Select
              onChange={(event: React.ChangeEvent<{ value: string }>) => {
                updateNoReasonFollowUp(event.target.value)
              }}
              value={noFollowUpReason}
              options={[
                {
                  label: 'Select Reason',
                  value: '',
                  disabled: true,
                  hidden: true,
                },
                {
                  label: 'Patient is cancelling',
                  value: NoFollowUpReasonEnum.PATIENT_IS_CANCELLING,
                },
                {
                  label: 'Patient is pregnant',
                  value: NoFollowUpReasonEnum.PATIENT_IS_PREGNANT,
                },
                {
                  label: 'Patient is becoming Nutrition only',
                  value:
                    NoFollowUpReasonEnum.PATIENT_IS_BECOMING_NUTRITION_ONLY,
                },
                {
                  label: 'Seeking external care',
                  value: NoFollowUpReasonEnum.SEEKING_EXTERNAL_CARE,
                },
                {
                  label: 'Appt already scheduled',
                  value: NoFollowUpReasonEnum.APPT_ALREADY_SCHEDULED,
                },
                {
                  label: 'Other',
                  value: NoFollowUpReasonEnum.OTHER,
                },
              ]}
            />

            {noFollowUpReason === 'Other' && (
              <div>
                <Form.Control
                  placeholder="Specify reason"
                  value={noFollowUpOtherReason}
                  onChange={updateNoFollowUpOtherReason}
                  type="text"
                />
              </div>
            )}
          </div>

          <PrimaryButton
            disabled={
              !noFollowUpReason ||
              (noFollowUpReason === 'Other' && !noFollowUpOtherReason)
            }
            theme="dark"
            onClick={handleConfirm}
          >
            Confirm
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}

export default memo(Orders)
